import { useCallback } from 'react'
import { ButtonSecondary } from '../../../../../../baseComponents/Button'
import { ModalMediumCentered } from '../../../../../../baseComponents/Modal'
import { ModalEditComponentProps } from '../../../../../../baseComponents/ModalsLayouts/types'
import ITHeaderText from '../../../../../../baseComponents/TwinTable/InputTableLayout/InputTableLeftColumn/Components/ITHeaderText'
import { renderHeaderElements } from '../../../../../../baseComponents/TwinTable/InputTableLayout/InputTableLeftColumn/InputTableLeftColumnForm'
import { InputTableLeftColumnChange } from '../../../../../../baseComponents/TwinTable/InputTableLayout/InputTableLeftColumn/types'
import { InputTableURLsTable, OrderHeaderURLs } from '../../../../../../baseComponents/TwinTable/InputTableLayout/types'
import TwinTrans from '../../../../../../baseComponents/TwinTrans'
import FormRenderer from '../../../../../../forms/FormRenderer'
import { FormElement, FormRender } from '../../../../../../forms/FormRenderer/types'
import { twinFetchPostJSON } from '../../../../../../utils/globals/data'
import { TwinDictionary } from '../../../../../../utils/globals/dictionary'
import { returnClassOfSpanTemplate } from '../../../../../../utils/globals/tailwind'
import useTwinTranslation from '../../../../../../utils/hooks/useTwinTranslation'
import { getConfigParam } from '../../../../../../utils/reducers/getters'
import { SetCompleteDataTy } from '../../../OrderLayout/types'
import { GroupVatPayload } from '../../../../../../utils/reducers/globals/groupVats'
import { BuyOrderTypeShippingKeys } from '@teinor/erp/types/company/order/buy/buyOrder'
import { BuyShippingModelType } from '@teinor/erp/types/company/order/buy/buyShipping'
import { getBuyShippingPriceField } from '../../../../../../app/erp/screens/Logistic/BuyParent/header/BuyHeaderShipping/functions'
import { flushSync } from 'react-dom'
import useBuyTypeShippingDict from '../../../../../../utils/hooks/buys/useBuyTypeShipping'
import { connect, ConnectedProps } from 'react-redux'
import { AllReduxPayloads } from '../../../../../../utils/reducers'
import withLoading from '../../../../../../utils/hoc/withLoading'
import { UserPermissions } from '../../../../../../baseComponents/CreateRoutering/types'
import { useOrderITHeaderHandleChangeLogic } from '../../../useOrderITHeaderHandleChange'

type ModalConfigShippingBuyOrderProps = ModalEditComponentProps & ReduxConfigShippingCreateStepHeader & { 
    isReadOnly?: boolean
    orderHeaderURLs: OrderHeaderURLs
    headerData: TwinDictionary
    getCompleteData: () => void
    userPermissions: UserPermissions
    setCompleteData: SetCompleteDataTy
    inputTableURLs: InputTableURLsTable
}

const ModalConfigShippingBuyOrder: React.FC<ModalConfigShippingBuyOrderProps> = ({ isReadOnly, ...rest }) => {
    if (isReadOnly) {
        return <ModalConfigShippingBuyOrderReadOnly {...{ isReadOnly, ...rest }} />
    }
    return (<ModalConfigShippingBuyOrderEdit { ...rest } />)
}

export type BuyOrderShippingModelTypeExtended = BuyShippingModelType & {
    typeShipping: BuyOrderTypeShippingKeys
    GroupVatId: number
}

const ModalConfigShippingBuyOrderReadOnly: React.FC<ModalConfigShippingBuyOrderProps> = ({ headerData, groupVats, setOpened }) => {
    const { t } = useTwinTranslation()
    const shipping = headerData?.OrderShipping
    const dictTypeShipping = useBuyTypeShippingDict()
    const extraElements: FormElement<keyof BuyShippingModelType>[] = []
    
    if (shipping) {
        extraElements.push(
            {
                name: 'priceVatIncluded',
                component: 'CheckboxMargin',
                label: t('priceWithVat', 'Precio con iva'),
                checked: headerData?.OrderShipping?.priceVatIncluded,
                className: returnClassOfSpanTemplate(2)
            }
        )
        const fieldPrice = shipping?.priceVatIncluded ? 'finalPriceWithTax' : 'initialPriceWithoutTax'
        extraElements.push(
            {
                name: fieldPrice,
                component: 'Custom',
                render: () => <ITHeaderText label={t('priceShipping', 'Precio transporte')} value={headerData?.OrderShipping?.[fieldPrice]} className={returnClassOfSpanTemplate(1)} />,
            },
            {
                name: 'trackingId',
                component: 'Custom',
                render: () => <ITHeaderText label={t('trackingNumber', 'N. seguimiento')} value={shipping?.trackingId || '-'} className={returnClassOfSpanTemplate(3)} />
            },
            {
                name: 'GroupVatId',
                component: 'Custom',
                render: () => <ITHeaderText label={t('vats', 'Impuestos')} value={groupVats?.[shipping?.GroupVatId]?.name || '-'} className={returnClassOfSpanTemplate(3)} />
            },
        )
    }
    
    const sections: FormRender<any> = [
        {
            cols: 3,
            className: 'modal_config_shipping_form text-start gap-x-30',
            elements: [
                {
                    name: 'type',
                    component: 'Custom',
                    render: () => <ITHeaderText label={t('typeShipping', 'Tipo de envío')} value={dictTypeShipping[headerData.typeShipping]?.name || ''} className={returnClassOfSpanTemplate(3)} />
                },
                ...extraElements
            ]
        }
    ]
    const parsedFields = renderHeaderElements(sections, async () => {}, headerData)
    return (
        <ModalMediumCentered opened={true} setOpened={setOpened} onClickOut={true} className='modal_config_shipping modal_order_sell_layout gap-y-19 flex_height'>
            <h2 className='flex justify-start regular20'><TwinTrans transKey='infoShipping'>Información del transporte</TwinTrans></h2>
            <div className=' modal_negative_margin flex flex-auto flex-col'>
                <FormRenderer sections={parsedFields} className='flex-auto overflow-auto' />
            </div>
            <div className='flex justify-end gap-x-33 gap-y-20 buttons_modal_config_shipping'>
                <ButtonSecondary onClick={() => setOpened(null)} className='regular14 mt-10'>
                    <TwinTrans transKey='accept'>Aceptar</TwinTrans>
                </ButtonSecondary>
            </div>
        </ModalMediumCentered>
    )
}

const ModalConfigShippingBuyOrderEdit: React.FC<ModalConfigShippingBuyOrderProps> = ({ headerData, groupVats, setOpened, ...logic }) => {
    const { t } = useTwinTranslation()
    const { handleChange } = useOrderITHeaderHandleChangeLogic({ setCompleteData: logic?.setCompleteData, updateDataURL: logic?.inputTableURLs.updateHeaderData })
    const { dictTypeShipping, deciderHandleChange, dynamicFormData, defaultBuyShippingGroupVat, updateBuyShippingBack, handleChangeVatBuyShipping } = useModalConfigShippingBuyOrderLogic({ headerData, handleChange, ...logic })

    const extraElements: FormElement<keyof BuyShippingModelType>[] = []
    if (dynamicFormData.existsBuyShipping) {
        extraElements.push(
            {
                name: 'priceVatIncluded',
                component: 'CheckboxMargin',
                label: t('priceWithVat', 'Precio con iva'),
                checked: headerData?.OrderShipping?.priceVatIncluded,
                className: 'mb-15 ' + returnClassOfSpanTemplate(3)
            }
        )
        const fieldPrice = headerData.OrderShipping?.priceVatIncluded ? 'finalPriceWithTax' : 'initialPriceWithoutTax'
        extraElements.push(
            {
                name: fieldPrice,
                component: 'InputNumberWithLabelMargin',
                label: t('priceShipping', 'Precio transporte'),
                value: headerData?.OrderShipping?.[fieldPrice] || 0,
                onChange: (value) => deciderHandleChange(headerData?.OrderShipping?.id, fieldPrice, value),
                onBlur: () => updateBuyShippingBack(fieldPrice),
                className: returnClassOfSpanTemplate(1)
            },
            {
                name: 'trackingId',
                component: 'InputWithLabelMargin',
                label: t('trackingNumber', 'N. seguimiento'),
                value: headerData?.OrderShipping?.trackingId || '',
                onChange: (value) => deciderHandleChange(headerData?.OrderShipping?.id, 'trackingId', value),
                onBlur: () => updateBuyShippingBack('trackingId'),
                className: returnClassOfSpanTemplate(2)
            },
            {
                name: 'GroupVatId',
                label: t('groupVat', 'Grupo de impuestos'),
                component: 'CustomSelectWithSearchBar',
                items: groupVats || {},
                value: headerData?.OrderShipping?.GroupVatId || defaultBuyShippingGroupVat,
                onChange: handleChangeVatBuyShipping,
                required: true,
                className: returnClassOfSpanTemplate(3)
            },
        )
    }

    const sections: FormRender<any> = [
        {
            cols: 3,
            className: 'modal_config_shipping_form text-start gap-x-30',
            elements: [
                {
                    name: 'typeShipping',
                    label: t('typeShipping', 'Tipo de envío'),
                    component: 'CustomSelectWithSearchBar',
                    items: dictTypeShipping,
                    required: true,
                    className: returnClassOfSpanTemplate(3)
                },
                ...extraElements,
            ]
        }
    ]
    const parsedFields = renderHeaderElements(sections, deciderHandleChange, headerData)
    return (
        <ModalMediumCentered opened={true} setOpened={setOpened} onClickOut={true} className='modal_config_shipping modal_order_sell_layout gap-y-19 flex_height'>
            <h2 className='flex justify-start regular20'><TwinTrans transKey='configShipping'>Configurar transporte</TwinTrans></h2>
            <div className=' modal_negative_margin flex flex-auto flex-col'>
                <FormRenderer sections={parsedFields} className='flex-auto px-30 overflow-x-visible' />
            </div>
            <div className='flex justify-end gap-x-33 gap-y-20 buttons_modal_config_shipping'>
                <ButtonSecondary onClick={() => setOpened(null)} className='regular14 mt-10'>
                    <TwinTrans transKey='accept'>Aceptar</TwinTrans>
                </ButtonSecondary>
            </div>
        </ModalMediumCentered>
    )
}

interface ModalConfigShippingBuyOrderLogicProps {
    setCompleteData: SetCompleteDataTy
    headerData: TwinDictionary
    handleChange: InputTableLeftColumnChange
    getCompleteData: () => void
    orderHeaderURLs: OrderHeaderURLs
}

const useModalConfigShippingBuyOrderLogic = ({ setCompleteData, headerData, handleChange, getCompleteData, orderHeaderURLs }: ModalConfigShippingBuyOrderLogicProps) => {
    const defaultBuyShippingGroupVat = getConfigParam('buyShippingGroupVatId')
    const dictTypeShipping = useBuyTypeShippingDict()
    const dynamicFormData = useDynamicFormData(headerData)
    const handleChangeBuyShipping: InputTableLeftColumnChange = useCallback(async (id: any, field: string, value: string | number) => {

        if ((field === 'initialPriceWithoutTax' || field === 'weight' || field === 'finalPriceWithTax') && value === '') {
            value = 0
        }
        setCompleteData((old: any) => {
            if (!old) {
                return null
            }
            const myData = JSON.parse(JSON.stringify(old))
            myData.header['OrderShipping'] = { ...myData.header['OrderShipping'], [field]: value }
            return { ...myData }
        })
    }, [setCompleteData])

    const updateBuyShippingBack = useCallback(async (field: string) => {
        await twinFetchPostJSON(orderHeaderURLs.updateShipping, {
            id: headerData?.OrderShippingId,
            'OrderId': headerData?.id,
            [field]: headerData.OrderShipping[field]
        })
    }, [headerData, orderHeaderURLs.updateShipping])

    const handleChangeBuyShippingPriceVat = useCallback(async (id: any, value: boolean) => {
        await flushSync(async () => {
            const { newPriceField, oldPriceField } = getBuyShippingPriceField(value)
            let newPrice = 0
            await setCompleteData((old: any) => {
                if (!old) {
                    return null
                }
                const myData = JSON.parse(JSON.stringify(old))
                newPrice = myData.header['OrderShipping'][oldPriceField]
                myData.header['OrderShipping'] = { ...myData.header['OrderShipping'], priceVatIncluded: value, [newPriceField]: newPrice }
                return { ...myData }
            })
            await twinFetchPostJSON(orderHeaderURLs.updateShipping, {
                id: headerData?.OrderShippingId,
                'OrderId': headerData?.id,
                priceVatIncluded: value,
                [newPriceField]: newPrice,
            })
        })
    }, [setCompleteData, headerData.id, headerData?.OrderShippingId, orderHeaderURLs.updateShipping])

    const handleCreateBuyShipping = useCallback(async (field: string, value: any) => {
        const result = await twinFetchPostJSON(orderHeaderURLs.createShipping, { 'OrderId': headerData?.id, [field]: value, CurrencyId: headerData.CurrencyId, typePrice: 1, GroupVatId: defaultBuyShippingGroupVat })
        if (result) {
            setCompleteData((old: any) => {
                if (!old) {
                    return null
                }
                const myData = JSON.parse(JSON.stringify(old))
                myData.header['OrderShipping'] = result
                myData.header['OrderShippingId'] = result.id
                return { ...myData }
            })
        }
    }, [setCompleteData, headerData, defaultBuyShippingGroupVat, orderHeaderURLs.createShipping])

    const handleChangeVatBuyShipping = useCallback(async (value: any) => {
        const result = await twinFetchPostJSON(orderHeaderURLs.updateShipping, {
            id: headerData?.OrderShippingId,
            'OrderId': headerData?.id,
            'GroupVatId': value
        })
        if (result) {
            getCompleteData()
        }
    }, [headerData, getCompleteData, orderHeaderURLs.updateShipping])


    const deciderHandleChange: InputTableLeftColumnChange = useCallback(async (id, field, value) => {
        if (field === 'priceVatIncluded') {
            await handleChangeBuyShippingPriceVat(id, value)
        } else if (field !== 'typeShipping') {
            if (headerData?.OrderShipping?.id) {
                await handleChangeBuyShipping(headerData?.OrderShippingId, field, value)
            }
        } else {
            await handleChange(id, field, value)
            if (String(value) === '0' && !headerData?.OrderShipping?.id) {
                await handleCreateBuyShipping(field, value)
            } else {
                setCompleteData((old) => {
                    if (!old) {
                        return null
                    }
                    const myData = JSON.parse(JSON.stringify(old))
                    myData.header['OrderShipping'] = undefined
                    myData.header['OrderShippingId'] = undefined
                    return { ...myData }
                })
            }
            getCompleteData()
        }
    }, [headerData, handleChange, setCompleteData, handleChangeBuyShipping, handleCreateBuyShipping, getCompleteData, handleChangeBuyShippingPriceVat])

    return { dictTypeShipping, deciderHandleChange, dynamicFormData, defaultBuyShippingGroupVat, updateBuyShippingBack, handleChangeVatBuyShipping }
}

const useDynamicFormData = (headerData: TwinDictionary) => {
    return {
        existsBuyShipping: headerData?.typeShipping === 0,
    }
}

const configShippingCreateStepHeaderDispatch = {
    setGroupVats: (payload: GroupVatPayload) => ({ type: 'CHANGE_GROUPVAT', payload }),
}

export type ReduxConfigShippingCreateStepHeader = ConnectedProps<typeof configShippingCreateStepHeaderConnect>
const mapConfigShippingCreateStepHeaderConnector = (state: AllReduxPayloads) => ({ groupVats: state.groupVats })
const configShippingCreateStepHeaderConnect = connect(mapConfigShippingCreateStepHeaderConnector, configShippingCreateStepHeaderDispatch)
const configShippingCreateStepHeaderConnectLoading = withLoading(ModalConfigShippingBuyOrder, [{ fetchUrl: '/api/app/config/vat/getAllGroupsVat', propName: 'groupVats', setFunctionName: 'setGroupVats' }])
const ModalConfigShippingBuyOrderCreateStepHeaderConnect = configShippingCreateStepHeaderConnect(configShippingCreateStepHeaderConnectLoading)

export default ModalConfigShippingBuyOrderCreateStepHeaderConnect